import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import Toast from '../../components/Toast';
import AddProspectModal from '../../components/prospection/AddProspectModal.jsx';
import Nr2jOrgCombobox from '../../components/nr2j/Nr2jOrgCombobox.jsx';
import { useToast } from '../../hooks/useToast';
import apiService from '../../services/api';
import {
  NR2J_CATALOG_BILLING_TYPES,
  NR2J_VAT_OPTIONS,
  centsToEuros,
  computeQuoteLineTotals,
  contactDisplayName,
  defaultDueAtInput,
  eurosToCents,
  formatCentsAsEuros,
  sumQuoteTotals,
  sumQuoteTotalsByBillingType,
} from '../../utils/nr2jConstants';
import { createNr2jProspectFromModal } from '../../utils/nr2jCreateProspect.js';

function emptyLine() {
  return {
    key: `${Date.now()}-${Math.random()}`,
    catalogItemId: '',
    name: '',
    description: '',
    quantity: 1,
    unitPriceHtCents: 0,
    priceEuros: '',
    vatRateBps: 2000,
    billingType: 'ONE_TIME',
    discountMode: 'none',
    discountPercent: '',
    discountEuros: '',
  };
}

function lineToPayload(line, index) {
  const unitPriceHtCents = line.priceEuros !== ''
    ? eurosToCents(line.priceEuros)
    : Number(line.unitPriceHtCents) || 0;
  const payload = {
    catalogItemId: line.catalogItemId || null,
    sortOrder: index,
    name: line.name.trim(),
    description: line.description?.trim() || null,
    quantity: Math.max(1, Number(line.quantity) || 1),
    unitPriceHtCents,
    vatRateBps: Number(line.vatRateBps),
    billingType: line.billingType,
    discountPercentBps: null,
    discountAmountHtCents: null,
  };
  if (line.discountMode === 'percent' && line.discountPercent !== '') {
    payload.discountPercentBps = Math.round(Number(line.discountPercent) * 100);
  } else if (line.discountMode === 'fixed' && line.discountEuros !== '') {
    payload.discountAmountHtCents = eurosToCents(line.discountEuros);
  }
  return payload;
}

export default function Nr2jInvoiceEditor() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const presetOrgId = searchParams.get('organizationId') || '';
  const { toasts, toast, removeToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [organizationId, setOrganizationId] = useState(presetOrgId);
  const [org, setOrg] = useState(null);
  const [contactId, setContactId] = useState('');
  const [dueAt, setDueAt] = useState(() => defaultDueAtInput());
  const [notesClient, setNotesClient] = useState('');
  const [notesInternal, setNotesInternal] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [lines, setLines] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createPrefill, setCreatePrefill] = useState('');

  const totals = useMemo(
    () => sumQuoteTotals(lines.map((l) => lineToPayload(l, 0))),
    [lines],
  );
  const billingTotals = useMemo(
    () => sumQuoteTotalsByBillingType(lines.map((l) => lineToPayload(l, 0))),
    [lines],
  );

  const loadMeta = useCallback(async () => {
    setLoading(true);
    try {
      const catalogRes = await apiService.listNr2jCatalog({ active: true }).catch(() => ({ items: [] }));
      setCatalog(catalogRes?.items || []);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger le catalogue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (!organizationId) {
      setOrg(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const detail = await apiService.getNr2jOrganization(organizationId);
        if (!cancelled) setOrg(detail?.organization || detail);
      } catch {
        if (!cancelled) setOrg(null);
      }
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  function selectOrganization(nextOrg) {
    const id = nextOrg?.id || '';
    setOrganizationId(id);
    setOrg(nextOrg || null);
    setContactId('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('organizationId', id);
      else next.delete('organizationId');
      return next;
    }, { replace: true });
  }

  function buildPayload() {
    return {
      organizationId,
      contactId: contactId || null,
      dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      notesClient: notesClient || null,
      notesInternal: notesInternal || null,
      paymentTerms: paymentTerms || null,
      paymentInstructions: paymentInstructions || null,
      lines: lines.map(lineToPayload),
    };
  }

  async function saveDraft() {
    if (!organizationId) {
      toast.error('Sélectionnez une société');
      return;
    }
    setBusy(true);
    try {
      const res = await apiService.createNr2jInvoice(buildPayload());
      const invoice = res?.invoice || res;
      toast.success(invoice.number ? `Brouillon créé — ${invoice.number}` : 'Brouillon créé');
      navigate(`/nr2j/facturation/${invoice.id}`, { replace: true });
    } catch (error) {
      toast.error(error?.message || 'Enregistrement impossible');
    } finally {
      setBusy(false);
    }
  }

  function addCatalogLine(itemId) {
    const item = catalog.find((c) => c.id === itemId);
    if (!item) return;
    setLines((prev) => [
      ...prev,
      {
        ...emptyLine(),
        catalogItemId: item.id,
        name: item.name,
        description: item.description || '',
        priceEuros: centsToEuros(item.unitPriceHtCents),
        unitPriceHtCents: item.unitPriceHtCents,
        vatRateBps: item.vatRateBps,
        billingType: item.billingType,
      },
    ]);
  }

  return (
    <div className="space-y-6" data-testid="nr2j-invoice-editor">
      <div className="sticky top-0 z-20 -mx-1 space-y-2 border-b border-gray-200 bg-white/95 px-1 py-3 backdrop-blur">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Link to="/nr2j/facturation" className="inline-flex items-center gap-1 text-sm text-indigo-700">
              <ArrowLeft className="h-4 w-4" /> Retour à la facturation
            </Link>
            <h1 className="mt-2 text-2xl font-bold text-gray-900">Nouvelle facture</h1>
            <p className="text-sm text-gray-600">
              Brouillon — numéro alloué à l’émission.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="nr2j-invoice-sticky-actions">
            <button
              type="button"
              disabled={busy || loading}
              onClick={saveDraft}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              Enregistrer
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : (
        <div className="grid gap-6 xl:grid-cols-3">
          <div className="space-y-6 xl:col-span-2">
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Société</h2>
              <div className="mt-3 block text-sm">
                <span className="text-gray-500">Société *</span>
                <div className="mt-1">
                  <Nr2jOrgCombobox
                    value={organizationId}
                    selectedOrg={org}
                    disabled={busy}
                    onChange={(next) => selectOrganization(next)}
                    onCreateCompany={(prefill) => {
                      setCreatePrefill(prefill || '');
                      setCreateOpen(true);
                    }}
                  />
                </div>
              </div>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Contact</span>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={contactId}
                  onChange={(e) => setContactId(e.target.value)}
                >
                  <option value="">— (email facturation / principal)</option>
                  {(org?.contacts || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {contactDisplayName(c) || c.id}
                      {c.email ? ` (${c.email})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Échéance</span>
                <input
                  type="date"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                  data-testid="nr2j-invoice-due-at"
                />
                <span className="mt-1 block text-xs text-gray-400">Prérempli à J+30 — modifiable</span>
              </label>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Lignes</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <select
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) {
                      addCatalogLine(e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">Ajouter depuis le catalogue…</option>
                  {catalog.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.code} — {item.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setLines((prev) => [...prev, emptyLine()])}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <Plus className="h-4 w-4" /> Ligne libre
                </button>
              </div>
              <div className="mt-4 space-y-4">
                {lines.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucune ligne.</p>
                ) : (
                  lines.map((line, idx) => {
                    const lineTotals = computeQuoteLineTotals(lineToPayload(line, 0));
                    return (
                      <div key={line.key} className="rounded-lg border border-gray-100 p-3">
                        <div className="grid gap-2 sm:grid-cols-2">
                          <label className="block text-sm sm:col-span-2">
                            <span className="text-gray-500">Désignation *</span>
                            <input
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.name}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, name: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Qté</span>
                            <input
                              type="number"
                              min="1"
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.quantity}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, quantity: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Prix unitaire HT (€)</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.priceEuros}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, priceEuros: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">TVA</span>
                            <select
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.vatRateBps}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, vatRateBps: Number(e.target.value) } : l
                              )))}
                            >
                              {NR2J_VAT_OPTIONS.map((v) => (
                                <option key={v.bps} value={v.bps}>{v.label}</option>
                              ))}
                            </select>
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Facturation</span>
                            <select
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.billingType}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, billingType: e.target.value } : l
                              )))}
                            >
                              {NR2J_CATALOG_BILLING_TYPES.map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                          <span>
                            HT {formatCentsAsEuros(lineTotals.lineHtCents)} € · TTC{' '}
                            {formatCentsAsEuros(lineTotals.lineTtcCents)} €
                          </span>
                          <button
                            type="button"
                            className="text-rose-700 hover:underline"
                            onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <label className="block text-sm">
                <span className="text-gray-500">Conditions de paiement</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Instructions de paiement</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={paymentInstructions}
                  onChange={(e) => setPaymentInstructions(e.target.value)}
                />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Note client</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={notesClient}
                  onChange={(e) => setNotesClient(e.target.value)}
                />
              </label>
              <label className="mt-3 block text-sm">
                <span className="text-gray-500">Note interne</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={notesInternal}
                  onChange={(e) => setNotesInternal(e.target.value)}
                />
              </label>
            </section>
          </div>

          <aside className="space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Totaux</h2>
              <div className="mt-3 space-y-4 text-sm">
                {billingTotals.map((block) => (
                  <div key={block.billingType}>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {block.label}
                    </p>
                    <dl className="space-y-1">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">HT{block.periodSuffix}</dt>
                        <dd className="font-medium">
                          {formatCentsAsEuros(block.totalHtCents)} €{block.periodSuffix}
                        </dd>
                      </div>
                      <div className="flex justify-between border-t border-gray-100 pt-1">
                        <dt className="font-semibold text-gray-900">TTC{block.periodSuffix}</dt>
                        <dd className="font-bold text-gray-900">
                          {formatCentsAsEuros(block.totalTtcCents)} €{block.periodSuffix}
                        </dd>
                      </div>
                    </dl>
                  </div>
                ))}
                {billingTotals.length === 0 ? (
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Total HT</dt>
                      <dd className="font-medium">{formatCentsAsEuros(totals.totalHtCents)} €</dd>
                    </div>
                    <div className="flex justify-between border-t border-gray-100 pt-2">
                      <dt className="font-semibold text-gray-900">Total TTC</dt>
                      <dd className="font-bold text-gray-900">{formatCentsAsEuros(totals.totalTtcCents)} €</dd>
                    </div>
                  </dl>
                ) : null}
              </div>
            </div>
          </aside>
        </div>
      )}

      <AddProspectModal
        open={createOpen}
        busy={createBusy}
        title="Créer une nouvelle société"
        initialBusinessName={createPrefill}
        submitLabel="Créer la société"
        onClose={() => {
          setCreateOpen(false);
          setCreatePrefill('');
        }}
        onSubmit={async (payload) => {
          setCreateBusy(true);
          try {
            const { organization } = await createNr2jProspectFromModal(apiService, payload);
            if (!organization?.id) {
              throw new Error('Société créée mais identifiant manquant');
            }
            const detail = await apiService.getNr2jOrganization(organization.id);
            selectOrganization(detail?.organization || detail);
            setCreateOpen(false);
            setCreatePrefill('');
            toast.success('Société créée');
          } finally {
            setCreateBusy(false);
          }
        }}
      />

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
